import { animate, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AfterViewInit, Component, computed, effect, ElementRef, input, OnInit, output, Signal, signal, ViewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';

import * as Hammer from 'hammerjs';
import { map, of, startWith, switchMap } from 'rxjs';
import { QuestionOption } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

@Component({
  selector: 'empathy-carousel-question-multi-choice-view',
  templateUrl: 'carousel-multi-choice-question-view.component.html',
  styleUrls: ['./carousel-multi-choice-question-view.component.scss', './_carousel-multi-choice-question-view-theme.component.scss'],
  animations: [
    trigger('stagger', [
      transition(':enter', [style({ transform: 'translateX(100%)', opacity: 0 }), animate('250ms cubic-bezier(.17,.67,1,1.04)')]),
      transition(':leave', [
        style({ transform: 'translateX(-100%)', opacity: 1, display: 'none' }),
        animate('250ms cubic-bezier(.17,.67,1,1.04)', style({ opacity: 0, display: 'none' }))
      ])
    ])
  ]
})
export class CarouselMultiChoiceQuestionViewComponent implements OnInit, AfterViewInit {
  @ViewChild('carouselSwipeContainer') myBtn: ElementRef;

  question = input<Question>();
  questionsGrp = input<FormGroup>();

  forceStop = output<boolean>();

  isPhone$: Signal<boolean>;

  activeCard$ = signal(0);
  useMedia$ = computed(() => this.question().visibleOptions.some((option) => !!option.stimulus?.mediaId));
  visibleCards$ = computed(() => this.question().visibleCards);
  visibleOptions$ = computed(() => this.question().visibleOptions);

  canGoNext$ = computed(() => {
    const selectedValues = this.currentCardSelectedValues$() ?? [];
    return selectedValues.length > 0 && selectedValues.length >= this.question().minLength;
  });

  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  otherOption: Partial<QuestionOption> = {
    value: 'OTHER',
    text: 'Other'
  };

  private currentCardValueCtrl$ = computed(
    () => this.questionsGrp().get(this.visibleCards$()[this.activeCard$()]?.id)?.get('value') as FormControl<string[]> | null
  );

  private currentCardSelectedValues$ = toSignal(
    toObservable(this.currentCardValueCtrl$).pipe(switchMap((control) => (control ? control.valueChanges.pipe(startWith(control.value ?? [])) : of([])))),
    { initialValue: [] as string[] }
  );

  private phoneBreakpoints = [Breakpoints.XSmall, Breakpoints.Small];

  constructor(breakpointObserver: BreakpointObserver) {
    this.isPhone$ = toSignal(
      breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge]).pipe(
        map((result) => {
          for (const queryPoint of Object.keys(result.breakpoints)) {
            if (result.breakpoints[queryPoint]) {
              return this.phoneBreakpoints.includes(queryPoint);
            }
          }

          return false;
        })
      ),
      { initialValue: false }
    );

    effect(() => {
      this.forceStop.emit(this.activeCard$() === this.question().visibleCards.length);
    });
  }

  private get selectedValues(): string[] {
    return this.questionsGrp().get(this.question().visibleCards[this.activeCard$()].id).get('value').value || [];
  }

  private get exclusiveOptionValues(): string[] {
    const configuredExclusiveOptions = this.question()
      .options.filter((option) => option.exclusiveInd)
      .map((option) => option.value);

    return [...configuredExclusiveOptions];
  }

  ngOnInit(): void {
    this.forceStop.emit(this.question().requiredInd);
  }

  ngAfterViewInit(): void {
    const switcher = this.myBtn.nativeElement;
    const mc = new Hammer(switcher);

    mc.on('swipeleft', () => {
      this.onNext();
    });

    mc.on('swiperight', () => {
      this.onPrev();
    });
  }

  getQuestionGroupForm = (control: unknown): FormControl => this.questionsGrp().get(control as string) as FormControl;

  onNext(): void {
    this.questionsGrp().get(this.question().visibleCards[this.activeCard$()].id).markAllAsTouched();
    if (this.questionsGrp().get(this.question().visibleCards[this.activeCard$()].id).invalid) {
      return;
    }

    if (this.activeCard$() < this.question().visibleCards.length - 1 && this.canGoNext$()) {
      this.activeCard$.update((value) => value + 1);
    }
  }

  onPrev(): void {
    if (this.activeCard$() > 0) {
      this.activeCard$.update((value) => value - 1);
    }
  }

  onMediaChecked(option: Partial<QuestionOption>): void {
    const index = this.selectedValues.findIndex((opt) => opt === option.value);

    this.onChecked({ checked: index === -1 }, option);
  }

  onChecked(event: { checked: boolean }, option: Partial<QuestionOption>): void {
    let currentValues = this.selectedValues;
    if (event.checked) {
      if (
        this.question().maxLength > 0 &&
        this.questionsGrp().get(this.visibleCards$()[this.activeCard$()].id).get('value').value?.length === this.question().maxLength
      ) {
        return;
      }
      this.clearExclusiveOptions(currentValues);
      if (option.exclusiveInd) {
        currentValues = [];
      }
      currentValues = [...currentValues, option.value];
    } else {
      const uncheckedOptionIndex = currentValues.findIndex((opt) => opt === option.value);
      currentValues.splice(uncheckedOptionIndex, 1);
    }

    this.questionsGrp()
      .get(this.question().visibleCards[this.activeCard$()].id)
      .get('value')
      .setValue([...currentValues]);

    if (!currentValues.includes(this.otherOption.value)) {
      this.questionsGrp().get(this.question().visibleCards[this.activeCard$()].id).get('otherText').disable();
    } else {
      this.questionsGrp().get(this.question().visibleCards[this.activeCard$()].id).get('otherText').enable();
    }
  }

  private clearExclusiveOptions(currentValue: string[]): void {
    this.exclusiveOptionValues.forEach((value) => {
      const index = currentValue.findIndex((opt) => opt === value);
      if (index > -1) {
        currentValue.splice(index, 1);
      }
    });
  }
}
